import { useEffect, useRef } from 'react';
import { useClerk } from '@clerk/clerk-react';

// 跨标签页通信的 key
const ACTIVITY_STORAGE_KEY = 'clerk_last_activity';
const LOGOUT_STORAGE_KEY = 'clerk_inactivity_logout';

/**
 * 不活跃超时 Hook（支持跨标签页同步）
 * 当用户在指定时间内没有任何操作时，自动登出
 * 
 * ⭐ 改进：跨标签页同步活动状态
 * - 任何标签页的活动都会重置所有标签页的计时器
 * - 避免一个标签页活跃，其他标签页超时导致全部登出
 * - 使用 localStorage 和 storage 事件实现跨标签页通信
 * 
 * @param timeoutMinutes - 超时时间（分钟）
 * @param enabled - 是否启用（默认 true）
 */
export const useInactivityTimeout = (
  timeoutMinutes: number = 10,
  enabled: boolean = true
) => {
  const { signOut } = useClerk();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastActivityRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!enabled) return;

    const timeoutMs = timeoutMinutes * 60 * 1000; // 转换为毫秒

    // 🎯 更新活动时间（同步到 localStorage，其他标签页可以读取）
    const updateLastActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      try {
        localStorage.setItem(ACTIVITY_STORAGE_KEY, now.toString());
      } catch (e) {
        console.warn('无法写入 localStorage:', e);
      }
    };

    // 🎯 检查是否超时（考虑其他标签页的活动）
    const checkTimeout = () => {
      try {
        // 从 localStorage 读取最后活动时间（可能由其他标签页更新）
        const storedActivity = localStorage.getItem(ACTIVITY_STORAGE_KEY);
        const lastActivity = storedActivity ? parseInt(storedActivity, 10) : Date.now();
        
        // 计算不活跃时间
        const inactiveTime = Date.now() - lastActivity;
        
        // 如果超时，执行登出
        if (inactiveTime >= timeoutMs) {
          console.log('⏰ 用户不活跃超时，自动登出', {
            inactiveMinutes: Math.round(inactiveTime / 60000),
            threshold: timeoutMinutes,
          });
          
          // 通知所有标签页即将登出
          localStorage.setItem(LOGOUT_STORAGE_KEY, Date.now().toString());
          
          // 执行登出
          signOut().then(() => {
            alert('由于长时间未操作，已自动登出。');
          }).catch((error) => {
            console.error('自动登出失败:', error);
          });
        }
      } catch (e) {
        console.error('检查超时失败:', e);
      }
    };

    // 重置计时器
    const resetTimer = () => {
      // 清除旧的计时器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // 🎯 更新活动时间（跨标签页同步）
      updateLastActivity();

      // 设置新的计时器
      timeoutRef.current = setTimeout(checkTimeout, timeoutMs);
    };

    // 监听的用户活动事件
    const events = [
      'mousedown',   // 鼠标点击
      'mousemove',   // 鼠标移动
      'keypress',    // 键盘输入
      'scroll',      // 滚动
      'touchstart',  // 触摸（移动设备）
      'click',       // 点击
    ];

    // 添加节流，避免频繁重置计时器
    let throttleTimeout: NodeJS.Timeout;
    const throttledResetTimer = () => {
      if (!throttleTimeout) {
        resetTimer();
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null as any;
        }, 1000); // 1秒内只重置一次
      }
    };

    // 绑定事件监听
    events.forEach(event => {
      window.addEventListener(event, throttledResetTimer);
    });

    // 🎯 监听其他标签页的活动（通过 storage 事件）
    const handleStorageChange = (e: StorageEvent) => {
      // 其他标签页更新了最后活动时间
      if (e.key === ACTIVITY_STORAGE_KEY && e.newValue) {
        const otherTabActivity = parseInt(e.newValue, 10);
        if (otherTabActivity > lastActivityRef.current) {
          // 其他标签页有活动，重置当前标签页的计时器
          console.log('🔄 检测到其他标签页活动，重置计时器');
          resetTimer();
        }
      }
      
      // 其他标签页触发了登出
      if (e.key === LOGOUT_STORAGE_KEY && e.newValue) {
        console.log('🚪 其他标签页触发登出，同步登出当前标签页');
        signOut();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    // 🎯 定期检查（每分钟检查一次，作为备份机制）
    checkIntervalRef.current = setInterval(() => {
      checkTimeout();
    }, 60 * 1000); // 每分钟检查一次

    // 初始化计时器
    resetTimer();

    // 清理函数
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, throttledResetTimer);
      });
      window.removeEventListener('storage', handleStorageChange);
      
      // 清理 localStorage（可选）
      // localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    };
  }, [timeoutMinutes, enabled, signOut]);

  // 返回当前状态（可选）
  return {
    lastActivity: lastActivityRef.current,
    getInactiveTime: () => Date.now() - lastActivityRef.current,
  };
};

